import { useEffect, useState } from "react";
import { Label, Form, FormGroup } from "reactstrap";
import { useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import toast from "react-hot-toast";

import { HeadingWithBackArrow } from "../../../components/HeadingWithBackArrow";
import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";

import typography from "../typography";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { ConfirmationModal } from "../../../components/CustomModal";
import ModalSuccessImage from "../../../assets/images/app/common/aou-modal-success-img.svg";
import { createAudioVideoContent } from "../../../api/api.service";
import { initializeMediaUtil, uploadOnS3, finalizeMediaUtil } from "../../../utils/mediaUtils";
import { validationSchema } from "../validation";
import {
    ACTIVITY_SLOT_OPTIONS,
    ALLOWED_IMAGE_EXTENSIONS,
    ALLOWED_MEDIA_EXTENSIONS,
    CONTENT_TYPE_OPTIONS,
    RESET_CONTEXT_OPTIONS,
    SLOT_ELIGIBLE_STATE_KEYS,
    STATE_KEY_OPTIONS,
} from "../data";
import FormikErrorText from "../../../components/FormikErrorText/FormikErrorText";
import WizardStepper from "./WizardStepper";
import PreviewCard from "./PreviewCard";
import FileDropzone from "./FileDropzone";

// Presentation-only grouping of the same fields/validation this page has
// always had — see validation.js for the actual rules. `fields` is only
// used to ask "does formik.errors have an entry for anything in THIS step",
// so Next can be disabled without re-implementing any validation logic.
const WIZARD_STEPS = [
    { key: "basic", label: "Basic Details", fields: ["title", "author", "description"] },
    { key: "category", label: "Category & Type", fields: ["contentType", "stateKey", "activitySlot", "contexts"] },
    { key: "additional", label: "Additional Info", fields: ["goal", "teacherInstructions"] },
    { key: "upload", label: "Upload & Review", fields: [] },
];

export default function AddContent() {
    const history = useHistory();
    const location = useLocation();

    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [teacherGuideFile, setTeacherGuideFile] = useState(null);
    const [teacherGuideFileError, setTeacherGuideFileError] = useState("");
    const [teacherGuideUploadProgress, setTeacherGuideUploadProgress] = useState(0);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailFileError, setThumbnailFileError] = useState("");
    const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [isModalActive, setIsModalActive] = useState(false);

    const [currentStep, setCurrentStep] = useState(0);
    const [maxStepReached, setMaxStepReached] = useState(0);
    const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);

    const backButtonHandler = () => {
        history.push(APP_ROUTES.CONTENT_LIBRARY);
    };

    // Pre-selects the state whose "Add" button was clicked from the Listing
    // page's per-state view (?state=clarity), so uploading a second activity
    // onto the same card doesn't require re-picking it every time.
    const requestedStateKey = new URLSearchParams(location.search).get("state");
    const initialStateKey = STATE_KEY_OPTIONS.some((option) => option.value === requestedStateKey)
        ? requestedStateKey
        : STATE_KEY_OPTIONS[0].value;

    const formik = useFormik({
        initialValues: {
            title: "",
            author: "",
            description: "",
            goal: "",
            teacherInstructions: "",
            contentType: "VIDEO",
            stateKey: initialStateKey,
            // "" rather than a numeric default — an unpicked slot must fail
            // validation, not silently default to Card 2.
            activitySlot: "",
            contexts: [],
        },
        validationSchema,
        onSubmit: async (values) => {
            await handleSaveClick(values);
        },
    });

    // Seeds formik.errors from the full schema once, so the very first
    // step's Next button reflects real validity (e.g. blank Title) without
    // waiting for a blur event. Formik's own validateOnChange/validateOnBlur
    // keep it in sync from here on — this effect never needs to run again.
    useEffect(() => {
        formik.validateForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cosmetic only — lets the preview card show the actual chosen image
    // instead of a placeholder. Revoked on cleanup/replacement so this can't
    // leak object URLs across re-renders.
    useEffect(() => {
        if (!thumbnailFile) {
            setThumbnailPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(thumbnailFile);
        setThumbnailPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [thumbnailFile]);

    const isSlotEligible = SLOT_ELIGIBLE_STATE_KEYS.includes(formik.values.stateKey);

    const isStepValid = (stepIndex) => !WIZARD_STEPS[stepIndex].fields.some((field) => Boolean(formik.errors[field]));

    const goNext = () => {
        if (!isStepValid(currentStep)) {
            // Next is disabled whenever this is true, so this only runs as a
            // defensive fallback — but if it ever does, make sure the reason
            // is visible rather than silently doing nothing.
            const touchedPatch = {};
            WIZARD_STEPS[currentStep].fields.forEach((field) => {
                touchedPatch[field] = true;
            });
            formik.setTouched({ ...formik.touched, ...touchedPatch });
            return;
        }
        const next = Math.min(currentStep + 1, WIZARD_STEPS.length - 1);
        setCurrentStep(next);
        setMaxStepReached((prev) => Math.max(prev, next));
    };

    const goBack = () => setCurrentStep((step) => Math.max(step - 1, 0));

    const goToStep = (index) => {
        if (index <= maxStepReached) setCurrentStep(index);
    };

    const handleContextToggle = (value) => {
        const current = formik.values.contexts;
        const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        formik.setFieldValue("contexts", next);
    };

    const handleFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        const extension = selected.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_MEDIA_EXTENSIONS.includes(extension)) {
            setFileError(`Unsupported file type. Allowed: ${ALLOWED_MEDIA_EXTENSIONS.join(", ")}`);
            setFile(null);
            return;
        }

        setFileError("");
        setFile(selected);
    };

    const handleTeacherGuideFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        const extension = selected.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_MEDIA_EXTENSIONS.includes(extension)) {
            setTeacherGuideFileError(`Unsupported file type. Allowed: ${ALLOWED_MEDIA_EXTENSIONS.join(", ")}`);
            setTeacherGuideFile(null);
            return;
        }

        setTeacherGuideFileError("");
        setTeacherGuideFile(selected);
    };

    const handleThumbnailFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        const extension = selected.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_IMAGE_EXTENSIONS.includes(extension)) {
            setThumbnailFileError(`Unsupported file type. Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(", ")}`);
            setThumbnailFile(null);
            return;
        }

        setThumbnailFileError("");
        setThumbnailFile(selected);
    };

    // The shared Media model has no AUDIO variant — every upload here goes
    // through as MediaType.VIDEO regardless of the admin's chosen content
    // type. AudioVideoContent.contentType (selected in this form) is the
    // real source of truth for audio-vs-video, matching the backend's
    // deliberate simplification.
    const handleMediaUpload = async (selectedFile) => {
        const credentials = await initializeMediaUtil(selectedFile, false, "VIDEO");
        await uploadOnS3(selectedFile, credentials, setUploadProgress);
        return finalizeMediaUtil(credentials?.mediaId, false);
    };

    // Same upload flow as the activity video, tracked with its own progress
    // state — this video is always optional, so the caller only invokes this
    // when a teacherGuideFile was actually chosen.
    const handleTeacherGuideUpload = async (selectedFile) => {
        const credentials = await initializeMediaUtil(selectedFile, false, "VIDEO");
        await uploadOnS3(selectedFile, credentials, setTeacherGuideUploadProgress);
        return finalizeMediaUtil(credentials?.mediaId, false);
    };

    // Same upload flow again, IMAGE this time (see Account/EditProfile.js
    // for the other place in this app that already uploads an IMAGE the
    // same way) — always optional, only invoked when an admin actually
    // picked a thumbnail file rather than leaving it to auto-generate.
    const handleThumbnailUpload = async (selectedFile) => {
        const credentials = await initializeMediaUtil(selectedFile, false, "IMAGE");
        await uploadOnS3(selectedFile, credentials, setThumbnailUploadProgress);
        return finalizeMediaUtil(credentials?.mediaId, false);
    };

    const handleSaveClick = async (values) => {
        if (!file) {
            setFileError("Please choose a file to upload");
            return;
        }

        try {
            setIsSaving(true);
            const media = await handleMediaUpload(file);
            const teacherGuideMedia = teacherGuideFile ? await handleTeacherGuideUpload(teacherGuideFile) : null;
            const thumbnailMedia = thumbnailFile ? await handleThumbnailUpload(thumbnailFile) : null;

            const isSlotEligibleState = SLOT_ELIGIBLE_STATE_KEYS.includes(values.stateKey);

            await createAudioVideoContent({
                title: values.title.trim(),
                author: values.author?.trim() || undefined,
                description: values.description?.trim() || undefined,
                goal: values.goal?.trim() || undefined,
                teacherInstructions: values.teacherInstructions?.trim() || undefined,
                contentType: values.contentType,
                stateKey: values.stateKey,
                ...(isSlotEligibleState && { activitySlot: Number(values.activitySlot) }),
                mediaId: media.id,
                ...(teacherGuideMedia && { teacherGuideMediaId: teacherGuideMedia.id }),
                // Omitted entirely (not sent) when no file was chosen — the
                // backend's own fallback (auto-extract from the video, for
                // VIDEO content) only runs when thumbnailMediaId is absent.
                ...(thumbnailMedia && { thumbnailMediaId: thumbnailMedia.id }),
                contexts: values.contexts,
            });

            setIsModalActive(true);
        } catch (error) {
            console.error("Error creating content:", error);
            toast.error("Failed to create content. Please try again.");
        } finally {
            setIsSaving(false);
            setUploadProgress(0);
            setTeacherGuideUploadProgress(0);
            setThumbnailUploadProgress(0);
        }
    };

    const handleModalYes = () => {
        setIsModalActive(false);
        history.push(APP_ROUTES.CONTENT_LIBRARY);
    };

    return (
        <>
            <div className="wizard-page-heading">
                <HeadingWithBackArrow
                    backArrowClickHandler={backButtonHandler}
                    headingTitle={typography.ADD_CONTENT}
                    showToggle={false}
                />
            </div>

            <div className="wizard-container">
                <WizardStepper
                    steps={WIZARD_STEPS}
                    currentStep={currentStep}
                    maxStepReached={maxStepReached}
                    onStepClick={goToStep}
                />

                <Form onSubmit={formik.handleSubmit}>
                    <div className="wizard-body">
                        <div className="wizard-card">
                            {currentStep === 0 && (
                                <>
                                    <div className="wizard-step-title">Basic Details</div>
                                    <div className="wizard-step-subtitle">
                                        Add the main information about your activity. Keep it short and clear.
                                    </div>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-title">
                                            {typography.TITLE}
                                            <span className="wizard-required-badge">*</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">Use a clear and simple title, e.g. Foot Press.</div>
                                        <TextInput
                                            id="content-title"
                                            name="title"
                                            placeholder="e.g. Foot Press"
                                            value={formik.values.title}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className="edit-notification-form-input hide-default-icon"
                                            required
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="title" />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-author">
                                            {typography.AUTHOR}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">e.g. Dr. Smith — leave blank if not applicable.</div>
                                        <TextInput
                                            id="content-author"
                                            name="author"
                                            placeholder="e.g. Dr. Smith"
                                            value={formik.values.author}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className="edit-notification-form-input hide-default-icon"
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="author" />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-description">
                                            {typography.DESCRIPTION}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">Add a short description teachers can understand.</div>
                                        <textarea
                                            id="content-description"
                                            name="description"
                                            className="edit-notification-form-textarea"
                                            placeholder="Write a short description about this activity…"
                                            value={formik.values.description}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            rows={4}
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="description" />
                                    </FormGroup>
                                </>
                            )}

                            {currentStep === 1 && (
                                <>
                                    <div className="wizard-step-title">Category &amp; Type</div>
                                    <div className="wizard-step-subtitle">Choose where this content fits. This helps teachers find it easily.</div>

                                    <div className="wizard-field-row">
                                        <FormGroup className="wizard-field-group">
                                            <Label className="edit-notification-form-label" for="content-type">
                                                {typography.CONTENT_TYPE}
                                                <span className="wizard-required-badge">*</span>
                                            </Label>
                                            <div className="standard-typo-14-400 mb-2">Is this an audio or video activity?</div>
                                            <select
                                                id="content-type"
                                                name="contentType"
                                                className="edit-notification-form-input"
                                                value={formik.values.contentType}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                {CONTENT_TYPE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <FormikErrorText formikInstance={formik} fieldName="contentType" />
                                        </FormGroup>

                                        <FormGroup className="wizard-field-group">
                                            <Label className="edit-notification-form-label" for="content-state">
                                                {typography.STATE}
                                                <span className="wizard-required-badge">*</span>
                                            </Label>
                                            <div className="standard-typo-14-400 mb-2">Choose the focus area for this activity.</div>
                                            <select
                                                id="content-state"
                                                name="stateKey"
                                                className="edit-notification-form-input"
                                                value={formik.values.stateKey}
                                                onChange={(e) => {
                                                    // Card 1 for calm/focus is permanent; a slot picked
                                                    // for the old state is meaningless for a state
                                                    // that doesn't have slots at all, or just wrong
                                                    // for the other one — always start fresh.
                                                    formik.setFieldValue("activitySlot", "");
                                                    formik.handleChange(e);
                                                }}
                                                onBlur={formik.handleBlur}
                                            >
                                                {STATE_KEY_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <FormikErrorText formikInstance={formik} fieldName="stateKey" />
                                        </FormGroup>
                                    </div>

                                    {isSlotEligible && (
                                        <FormGroup className="wizard-field-group">
                                            <Label className="edit-notification-form-label" for="content-activity-slot">
                                                {typography.ACTIVITY_SLOT}
                                                <span className="wizard-required-badge">*</span>
                                            </Label>
                                            <div className="standard-typo-14-400 mb-2">Which card this activity replaces.</div>
                                            <select
                                                id="content-activity-slot"
                                                name="activitySlot"
                                                className="edit-notification-form-input"
                                                value={formik.values.activitySlot}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                <option value="" disabled>
                                                    Choose a card…
                                                </option>
                                                {ACTIVITY_SLOT_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <FormikErrorText formikInstance={formik} fieldName="activitySlot" />
                                        </FormGroup>
                                    )}

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label">Quick timing / context filters</Label>
                                        <div className="standard-typo-14-400 mb-2">
                                            Optional — select the situations where this activity is most useful.
                                        </div>
                                        <div className="wizard-checkbox-grid">
                                            {RESET_CONTEXT_OPTIONS.map((option) => (
                                                <label
                                                    key={option.value}
                                                    className="d-flex align-items-center gap-2 standard-typo-14-400"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formik.values.contexts.includes(option.value)}
                                                        onChange={() => handleContextToggle(option.value)}
                                                    />
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                        <FormikErrorText formikInstance={formik} fieldName="contexts" />
                                    </FormGroup>
                                </>
                            )}

                            {currentStep === 2 && (
                                <>
                                    <div className="wizard-step-title">Additional Information</div>
                                    <div className="wizard-step-subtitle">
                                        Add helpful details for teachers. These will be shown on the activity preview screen.
                                    </div>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-goal">
                                            {typography.GOAL}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">{typography.GOAL_HELP}</div>
                                        <TextInput
                                            id="content-goal"
                                            name="goal"
                                            placeholder="e.g. Helps students relax and reduce anxiety"
                                            value={formik.values.goal}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className="edit-notification-form-input hide-default-icon"
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="goal" />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-teacher-instructions">
                                            {typography.TEACHER_INSTRUCTIONS}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">{typography.TEACHER_INSTRUCTIONS_HELP}</div>
                                        <textarea
                                            id="content-teacher-instructions"
                                            name="teacherInstructions"
                                            className="edit-notification-form-textarea"
                                            placeholder="Write brief instructions for teachers on how to run this activity…"
                                            value={formik.values.teacherInstructions}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            rows={4}
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="teacherInstructions" />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-teacher-guide-file">
                                            {typography.TEACHER_GUIDE_VIDEO}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">{typography.TEACHER_GUIDE_VIDEO_HELP}</div>
                                        <FileDropzone
                                            id="content-teacher-guide-file"
                                            name="teacherGuideFile"
                                            accept={ALLOWED_MEDIA_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                                            acceptedExtensionsLabel={ALLOWED_MEDIA_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ")}
                                            file={teacherGuideFile}
                                            error={teacherGuideFileError}
                                            uploadProgress={teacherGuideUploadProgress}
                                            isUploading={isSaving && !!teacherGuideFile}
                                            onChange={handleTeacherGuideFileChange}
                                            onRemove={() => {
                                                setTeacherGuideFile(null);
                                                setTeacherGuideFileError("");
                                            }}
                                        />
                                    </FormGroup>
                                </>
                            )}

                            {currentStep === 3 && (
                                <>
                                    <div className="wizard-step-title">Upload Files &amp; Review</div>
                                    <div className="wizard-step-subtitle">Add your content file and thumbnail. Review all details before saving.</div>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-file">
                                            {typography.FILE}
                                            <span className="wizard-required-badge">*</span>
                                        </Label>
                                        <FileDropzone
                                            id="content-file"
                                            name="file"
                                            accept={ALLOWED_MEDIA_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                                            acceptedExtensionsLabel={ALLOWED_MEDIA_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ")}
                                            file={file}
                                            error={fileError}
                                            uploadProgress={uploadProgress}
                                            isUploading={isSaving}
                                            onChange={handleFileChange}
                                            onRemove={() => {
                                                setFile(null);
                                                setFileError("");
                                            }}
                                        />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-thumbnail-file">
                                            {typography.THUMBNAIL}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">{typography.THUMBNAIL_HELP}</div>
                                        <FileDropzone
                                            id="content-thumbnail-file"
                                            name="thumbnailFile"
                                            accept={ALLOWED_IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                                            acceptedExtensionsLabel={ALLOWED_IMAGE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ")}
                                            file={thumbnailFile}
                                            error={thumbnailFileError}
                                            uploadProgress={thumbnailUploadProgress}
                                            isUploading={isSaving && !!thumbnailFile}
                                            onChange={handleThumbnailFileChange}
                                            onRemove={() => {
                                                setThumbnailFile(null);
                                                setThumbnailFileError("");
                                            }}
                                        />
                                    </FormGroup>
                                </>
                            )}

                            <div className="wizard-footer">
                                <CustomButton
                                    title="← Back"
                                    type="button"
                                    color="light"
                                    onClick={goBack}
                                    disabled={currentStep === 0}
                                />
                                {currentStep < WIZARD_STEPS.length - 1 ? (
                                    <CustomButton
                                        key="wizard-next-btn"
                                        title="Next →"
                                        type="button"
                                        color="info"
                                        onClick={goNext}
                                        disabled={!isStepValid(currentStep)}
                                    />
                                ) : (
                                    <CustomButton
                                        key="wizard-save-btn"
                                        title={isSaving ? "Saving…" : "Save"}
                                        className="edit-notification-update-button"
                                        onClick={formik.handleSubmit}
                                        type="submit"
                                        loading={isSaving}
                                    />
                                )}
                            </div>
                        </div>

                        <PreviewCard
                            values={formik.values}
                            thumbnailPreviewUrl={thumbnailPreviewUrl}
                            contentFileName={file?.name}
                            teacherGuideFileName={teacherGuideFile?.name}
                            thumbnailFileName={thumbnailFile?.name}
                            detailed={currentStep === 3}
                        />
                    </div>
                </Form>
            </div>

            <ConfirmationModal
                isOpen={isModalActive}
                modalType="CONTENT_SAVED"
                onYes={handleModalYes}
                size="md"
                showNoButton={false}
                customImageSrc={ModalSuccessImage}
            />
        </>
    );
}
